import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, TextField, Grid, styled, Typography, Card, Avatar, IconButton, Box, Modal, CircularProgress, useTheme, Skeleton } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/authContext';
import { getIssuer, uploadProfileImageToAzure, updateIssuer, getUserContext } from '../../services/apiservice';
import "cropperjs/dist/cropper.css";
import Cropper from "react-cropper";
import SnackbarComponent from '../../utils/SnackbarComponent';
import { useQueryClient } from 'react-query';
import _ from 'lodash';
import { getSelectedRole } from '../../utils/auth';
import { useApiData } from '../../hooks/useApi';
import { linkedInUrlValidation, ProfileHeaderBand, SectionLabel, startIcon } from '../common/profile/profileFormUtils';

const RedAsterisk = styled('span')({
  color: 'red',
});


const AvatarContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
});

const EditIconButton = styled(IconButton)({
  position: 'absolute',
  bottom: 0,
  right: 0,
  backgroundColor: 'white',
  borderRadius: '50%',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  opacity: 1,
  width: 36,
  height: 36,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const IssuerProfile = () => {
  const [profileImage, setProfileImage] = useState(null);
  const theme = useTheme();
  const [issuerData, setIssuerData] = useState({
    IssuerName: '',
    IssuerRepFirstName: '',
    IssuerRepLastName: '',
    IssuerRepEmailID: '',
    IssuerWebsite: '',
    IssuerRepPhoneNumber: '',
    IssuerLinkedInProfileLink: '',
  });
  const queryClient = useQueryClient();
  const [originalFilename, setOriginalFilename] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropData, setCropData] = useState(null);
  const [cropper, setCropper] = useState();
  const { user, initials, avatarColor, setUser } = useAuth();
  const allowedRoles = ['Admin', 'Issuer'];
  const email = allowedRoles.includes(user.role) ? user.IssuerRepEmailID : user.UserEmail;
  const selectedRole = getSelectedRole()?.selectedRole || null;
  // const email = 
  // user.role === "Issuer" || (user.role === "User" && user.IssuerRepEmailID) 
  //   ? user.IssuerRepEmailID 
  //   : user.UserEmail;
  const issuerId = user.IssuerID;
  const { data: fetchedIssuer, loading } = useApiData(() => getIssuer(issuerId).then((res) => res.data), [user, issuerId]);

  useEffect(() => {
    if (fetchedIssuer) {
      setIssuerData(fetchedIssuer);
      setProfileImage(fetchedIssuer.IssuerLogoURL);
    }
  }, [fetchedIssuer]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleIssuerLogoChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const imageUrl = reader.result;
        setSelectedImage(imageUrl);
        setOriginalFilename(file.name);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarClick = () => {
    setIsModalOpen(true);
  };

  const handleUpload = async () => {
    if (typeof cropper !== "undefined") {
      const croppedData = cropper.getCroppedCanvas().toDataURL();
      const file = dataURLtoFile(croppedData, 'cropped-image.png', originalFilename);
      setCropData(file);
    }
  };

  const dataURLtoFile = (dataurl, filename, originalFilename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], originalFilename, { type: mime });
  };

  const handleClose = () => {
    setCropModalOpen(false);
    setFileInputKey((prevKey) => prevKey + 1);
    setSelectedImage(null);
    setCropper(null);
    setCropData(null);
  };
  useEffect(() => {
    const uploadCroppedImage = async () => {
      if (!cropData) return;
      setCropModalOpen(false);
      setUploading(true);
      try {
        const updatedImageUrl = await uploadProfileImageToAzure(cropData, issuerId, user.role);

        const img = new Image();
        img.src = updatedImageUrl;

        img.onload = () => {
          queryClient.invalidateQueries('issuerLogo');
          setFileInputKey((prevKey) => prevKey + 1);
        };
        const userContextResponse = await getUserContext(email, selectedRole);
        if (userContextResponse.success) {
          setUser({ ...userContextResponse.data.userContext, role: selectedRole });
          setSnackbarMessage('Issuer logo updated successfully!');
          setSnackbarSeverity('success');
          setProfileImage(updatedImageUrl);
        } else {
          setSnackbarMessage('Error fetching updated issuer context');
          setSnackbarSeverity('error');
        }

      } catch (error) {
        setSnackbarMessage('Error updating issuer logo');
        setSnackbarSeverity('error');
      } finally {
        setUploading(false);
        setSnackbarOpen(true);
        setCropper(null);
        setCropData(null);
      }
    };

    uploadCroppedImage();
  }, [cropData, user, setUser]);


  const validationSchema = Yup.object({
    IssuerName: Yup
      .string()
      .required('Issuer Name is required')
      .matches(/^[A-Za-z\s]+$/, 'Issuer Name must contain only alphabets'),
    IssuerRepFirstName: Yup
      .string()
      .required('First Name is required')
      .matches(/^[A-Za-z\s]+$/, 'First Name must contain only alphabets'),
    IssuerRepLastName: Yup
      .string()
      .required('Last Name is required')
      .matches(/^[A-Za-z\s]+$/, 'Last Name must contain only alphabets'),
    IssuerRepEmailID: Yup
      .string()
      .required('Email ID is required')
      .email('Invalid Email ID'),
    IssuerWebsite: Yup
      .string()
      .required('Website is required')
      .test('url', 'Invalid URL', url => {
        const urlRegex = /^(https?|ftp):\/\/[^\s]+$/;
        return urlRegex.test(url);
      }),
    IssuerRepPhoneNumber: Yup
      .string()
      .required('Contact Number is required'),
    IssuerLinkedInProfileLink: linkedInUrlValidation
  });

  return (
    <>
      <Helmet>
        <title>Issuer Settings</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Formik
        initialValues={issuerData || {}}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const updatedFields = Object.keys(values).reduce((acc, key) => {
              if (values[key] !== issuerData[key]) {
                acc[key] = values[key];
              }
              return acc;
            }, {});

            const hasChanges = Object.keys(updatedFields).some(key => updatedFields[key] !== issuerData[key]);

            if (!hasChanges) {
              setSnackbarSeverity('warning');
              setSnackbarMessage('Please update before submitting');
              setSnackbarOpen(true);
              return;
            }

            const response = await updateIssuer({ ...updatedFields, IssuerID: issuerId });

            if (response.success) {
              setSnackbarMessage('Issuer Profile updated successfully!');
              setSnackbarSeverity('success');
            } else {
              setSnackbarMessage('Failed to update Issuer Profile.');
              setSnackbarSeverity('error');
            }
            setIssuerData({ ...issuerData, ...updatedFields });
            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }


        }}
      >
        {({ errors, touched, isSubmitting, }) => (
          <Card sx={{ width: 'calc(100% - 40px)', maxWidth: 920, mx: 'auto', my: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
            <ProfileHeaderBand>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
                Issuer Profile
              </Typography>
              <Grid container justifyContent="center">
                <Grid item>
                  <Box sx={{ position: 'relative' }}>
                  {loading ? (
                    <Skeleton variant="circular" width={120} height={120} />
                  ) : (
                    <AvatarContainer>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={profileImage || undefined}
                          sx={{
                            width: 120,
                            height: 120,
                            opacity: uploading ? 0.5 : 1,
                            filter: uploading ? 'blur(2px)' : 'none',
                            backgroundColor: avatarColor,
                            display: profileImage ? 'block' : 'flex',
                            justifyContent: profileImage ? 'initial' : 'center',
                            alignItems: profileImage ? 'initial' : 'center',
                            fontSize: '3rem',
                            border: '3px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          }}
                          onClick={handleAvatarClick}
                        >
                          {!profileImage && initials}
                        </Avatar>
                        {uploading && (
                          <CircularProgress
                            size={40}
                            sx={{
                              position: 'absolute',
                              top: '35%',
                              left: '35%',
                              transform: 'translate(-35%, -35%)',
                              zIndex: 1,
                              color: 'white'
                            }}
                          />
                        )}
                        <EditIconButton
                          className="edit-icon"
                          onClick={() => document.getElementById('issuer-logo-input')?.click()}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            zIndex: 1,
                          }}
                        >
                          <CameraAltIcon />
                        </EditIconButton>
                      </Box>
                      <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.92)' }}>
                        {email}
                      </Typography>
                      <input
                        type="file"
                        id="issuer-logo-input"
                        key={fileInputKey}
                        onChange={handleIssuerLogoChange}
                        style={{ display: 'none' }}
                      />
                      {cropModalOpen && (
                        <Modal
                          open={cropModalOpen}
                          onClose={handleClose}
                          aria-labelledby="modal-title"
                          aria-describedby="modal-description"
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{
                              width: '400px',
                              height: '400px',
                              border: '1px solid #ddd',
                              borderRadius: '10px',
                              boxShadow: '0px 0px 10px rgba(0,0,0,0.2)',
                              display: 'flex',
                              marginLeft: 30,
                              justifyContent: 'center',
                              alignItems: 'center',
                              overflow: 'hidden'
                            }}>
                              <Cropper
                                zoomTo={0.5}
                                initialAspectRatio={1}
                                preview=".img-preview"
                                viewMode={1}
                                minCropBoxHeight={10}
                                minCropBoxWidth={10}
                                background={false}
                                responsive={true}
                                autoCropArea={1}
                                checkOrientation={false}
                                src={selectedImage}
                                onInitialized={(instance) => {
                                  setCropper(instance);
                                }}
                                style={{
                                  maxHeight: '100%',
                                  maxWidth: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '20px', }}>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ mr: 2 }}
                                onClick={() => {
                                  handleClose();
                                }}
                              >
                                {'Cancel'}
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ ml: 14 }}
                                onClick={handleUpload}
                              >
                                {'Upload'}
                              </Button>
                            </Box>
                          </Box>
                        </Modal>
                      )}
                      {isModalOpen && (
                        <Modal
                          open={isModalOpen}
                          onClose={() => setIsModalOpen(false)}
                          aria-labelledby="modal-title"
                          aria-describedby="modal-description"
                        >
                          <Box sx={{
                            position: 'absolute',
                            top: '48%',
                            left: '62.5%',
                            transform: 'translate(-70%, -70%)',
                            p: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                            [theme.breakpoints.down('sm')]: {
                              top: '40%',
                              left: '58%',
                              transform: 'translate(-60%, -45%)',
                              width: '90vw',
                              height: '80vh',
                            },
                          }}>
                            <img src={profileImage} alt="Issuer Logo" style={{
                              maxWidth: '100%',
                              maxHeight: '100vh',
                              objectFit: 'cover',
                              borderRadius: '10px',
                              width: '300px',
                              height: '300px',
                              [theme.breakpoints.down('sm')]: {
                                width: '100%',
                                height: '100%',
                              },
                            }} />
                            <IconButton onClick={() => setIsModalOpen(false)} sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              color: 'white',
                              [theme.breakpoints.down('sm')]: {
                                top: 5,
                                right: 5,
                              },
                            }}>
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Modal>
                      )}
                    </AvatarContainer>
                  )}
                </Box>
              </Grid>
            </Grid>
            </ProfileHeaderBand>

            <Box sx={{ p: 3 }}>
            <Form>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <SectionLabel>Organization</SectionLabel>
                </Grid>
                <Grid item xs={12}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Issuer Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerName"
                      fullWidth
                      disabled
                      InputProps={startIcon(<BusinessIcon fontSize="small" />)}
                      error={touched.IssuerName && Boolean(errors.IssuerName)}
                      helperText={touched.IssuerName && errors.IssuerName}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <SectionLabel>Representative</SectionLabel>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'First Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerRepFirstName"
                      fullWidth
                      InputProps={startIcon(<PersonIcon fontSize="small" />)}
                      error={touched.IssuerRepFirstName && Boolean(errors.IssuerRepFirstName)}
                      helperText={touched.IssuerRepFirstName && errors.IssuerRepFirstName}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Last Name'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerRepLastName"
                      fullWidth
                      InputProps={startIcon(<PersonIcon fontSize="small" />)}
                      error={touched.IssuerRepLastName && Boolean(errors.IssuerRepLastName)}
                      helperText={touched.IssuerRepLastName && errors.IssuerRepLastName}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Website'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerWebsite"
                      fullWidth
                      InputProps={startIcon(<LinkIcon fontSize="small" />)}
                      error={touched.IssuerWebsite && Boolean(errors.IssuerWebsite)}
                      helperText={touched.IssuerWebsite && errors.IssuerWebsite}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'Contact Number'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerRepPhoneNumber"
                      fullWidth
                      InputProps={startIcon(<PhoneIcon fontSize="small" />)}
                      error={touched.IssuerRepPhoneNumber && Boolean(errors.IssuerRepPhoneNumber)}
                      helperText={touched.IssuerRepPhoneNumber && errors.IssuerRepPhoneNumber}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <SectionLabel>Social</SectionLabel>
                </Grid>
                <Grid item xs={12}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Field
                      as={TextField}
                      label={
                        <>
                          {'LinkedIn Profile URL'} <RedAsterisk>*</RedAsterisk>
                        </>
                      }
                      name="IssuerLinkedInProfileLink"
                      fullWidth
                      placeholder="https://www.linkedin.com/in/your-name"
                      InputProps={startIcon(<LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />)}
                      error={touched.IssuerLinkedInProfileLink && Boolean(errors.IssuerLinkedInProfileLink)}
                      helperText={touched.IssuerLinkedInProfileLink && errors.IssuerLinkedInProfileLink}
                    />
                  )}
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center">
                  {loading ? (
                    <Skeleton variant="rectangular" width={90} height={42} sx={{ borderRadius: 1 }} />
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Form>
            </Box>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default IssuerProfile;